import React, { useMemo, useState } from "react";
import Immutable from "immutable";
import { Icon, Text, Button, Checkbox, Input } from "@showitinc/showit-wc";
import SiteSettingsStore from "../stores/SiteSettingsStore";
import useUserAssets, { getWOFFList } from "../hooks/query/useUserAssets";
import { formatFontName } from "../utils/FontUtils";
import AppConstants from "../constants/AppConstants";
import UIActions from "../actions/UIActions";
import styles from "./FontPanelUpload.module.css";
import sharedStyles from "./DesignSettings/DesignSettingsFont.module.css";
import FontPreview from "./FontPreview";

const { ASSET_LIBRARY_MODE_CHOOSER } = AppConstants;

/**
 * Lets a user upload a custom .WOFF font file (via the asset library chooser) and name it for use in the current design's font list.
 * @param {Object} props
 * @param {(fontName: string) => boolean} props.fontExists
 * @param {(font: Immutable.Map, fontName: string) => void} props.addFont
 * @param {Object} [props.designContext]
 */
const FontPanelUpload = ({ fontExists, addFont, designContext }) => {
  // Captured ONCE via the lazy initializer, exactly like the class version's constructor read.
  const [siteSettings] = useState(() => SiteSettingsStore.getInitialState());
  const { assets } = useUserAssets();
  const woffAssets = useMemo(() => getWOFFList(assets), [assets]);

  const [font, setFont] = useState(
    Immutable.Map({
      weight: 400,
      type: "custom",
    })
  );
  const [confirm, setConfirm] = useState(false);
  const [error, setError] = useState("");

  const _handlePropChange = (changes) => {
    setFont((prevFont) => prevFont.merge(changes));
    setError("");
  };

  const _addFont = () => {
    const fontName = font.get("name", "");

    if (fontName.length === 0) {
      setError("Please add a font name");
      return null;
    }
    if (fontExists(fontName)) {
      setError("Font name already exists");
      return null;
    }
    if (!confirm) {
      setError("Please acknowledge that you own a license to use this font");
      return null;
    }

    const assetURL = siteSettings.get("config").get("assetURL");
    const finalFont = font
      .set("family", fontName.trim())
      .set("src", font.get("src", "").replace(`${assetURL}/`, ""));

    addFont(finalFont, fontName);

    setFont(
      Immutable.Map({
        weight: 400,
        type: "custom",
      })
    );
    setConfirm(false);
    setError("");

    return null;
  };

  const _selectFont = (e) => {
    const { value } = e.target;
    let label = "";
    if (e.target && e.target.options && e.target.selectedIndex >= 0) {
      label = e.target.options[e.target.selectedIndex].text;
    }
    _handlePropChange({
      src: value,
      name: formatFontName(label),
    });
  };

  const _chooseFont = (fontSrc, fontName) => {
    _handlePropChange({
      src: fontSrc,
      name: formatFontName(fontName),
    });
  };

  const _setAsset = (asset) => {
    _chooseFont(asset.key, asset.asset_name);
  };

  const _toggleConfirm = () => {
    setConfirm((prev) => !prev);
  };

  const name = font.get("name");
  const src = font.get("src", "");

  const woffSelected = src.length > 0;

  const chooserConfig = {
    mode: ASSET_LIBRARY_MODE_CHOOSER,
    title: "Upload Font",
    filterExts: ["woff"],
    onSelect: _setAsset,
  };

  return (
    <div className={sharedStyles["font-type-wrapper"]}>
      <div className={sharedStyles["font-selection-wrapper"]}>
        <Text variant="heading-1" variantStyle="strong">
          Custom Fonts
        </Text>
        <Text variant="body-small">Upload a .WOFF font file below.</Text>
        <div className={styles["font-custom-upload-selection-wrapper"]}>
          <Text className={styles["font-custom-upload-label"]} variant="body-small">
            .WOFF File
          </Text>
          <div className={styles["font-custom-upload-input-wrapper"]}>
            <div className={styles["font-custom-upload-select-wrapper"]}>
              {woffAssets?.length > 0 && (
                <div className={styles["font-custom-upload-select-wrapper-inner"]}>
                  <select
                    className={styles["font-custom-upload-select"]}
                    value={src}
                    onChange={_selectFont}
                  >
                    <option value="">Select a font file...</option>
                    {woffAssets.map((asset) => (
                      <option key={asset.key} value={asset.key}>
                        {asset.asset_name}
                      </option>
                    ))}
                  </select>
                  <Icon
                    type="arrowChevronDown"
                    size="20"
                    className={styles["font-custom-upload-select-icon"]}
                  />
                </div>
              )}
              <Button
                className={styles["font-custom-upload-button"]}
                variant="outline"
                onClick={() => UIActions.toggleAssetChooser(chooserConfig)}
              >
                Upload Font File
              </Button>
            </div>
            <Text variant="small">
              Have a font file but incorrect format? Generate a .WOFF font file using the free
              generator at{" "}
              <a
                href="https://www.fontsquirrel.com/tools/webfont-generator"
                target="_blank"
                rel="noreferrer"
              >
                Font&nbsp;Squirrel
              </a>
              .
            </Text>
          </div>
        </div>
        <div className={styles["font-custom-upload-name-wrapper"]}>
          {woffSelected && (
            <>
              <Input
                className={styles["font-name-input"]}
                value={name}
                type="text"
                key="name"
                noPad
                label="Font Name"
                placeholder="Enter a custom font name"
                propName="name"
                propType="text"
                isRequired
                onSIInput={(e) => _handlePropChange({ name: formatFontName(e.target.value) })}
                title="Only uppercase and lowercase letters allowed"
              />
              <Text
                className={styles["font-custom-upload-name-description"]}
                variant="small"
                variantStyle="light"
              >
                Give the font a specific name, such as Arial Bold Italic.
              </Text>
            </>
          )}
        </div>

        {woffSelected && (
          <Checkbox
            className={styles["confirm-checkbox"]}
            checked={confirm}
            label="I own a license to use this font on my website"
            onSIChange={_toggleConfirm}
          />
        )}

        {error && (
          <div className="error" style={{ marginBottom: "1rem" }}>
            {error}
          </div>
        )}
        {woffSelected && (
          <Button variant="outline" disabled={!woffSelected || !confirm} onSIClick={_addFont}>
            Add Custom Font
          </Button>
        )}
      </div>
      <div className={sharedStyles["font-preview-wrapper"]}>
        {woffSelected && (
          <>
            <Text
              className={sharedStyles["font-preview-title"]}
              variant="body-small"
              variantStyle="strong"
            >
              Font Preview
            </Text>
            <div className={sharedStyles["font-preview-container"]}>
              <FontPreview
                justShowPreview
                font={font.set("family", name)}
                fontName={name}
                designContext={designContext}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default FontPanelUpload;
